import { isValidString } from "@/utils/string_formatter";
import { defineStore } from "pinia";
import { useImportLocationStore } from "@/store/import/import_location_store";
import { useScheduleLocationStore } from "@/store/schedule/schedule_location_store";
import { useExportLocationStore } from "@/store/export/export_location_store";
import { useEquipmentLocationStore } from "@/store/history/equipment_location_store";

export const useSearchIdsStore = defineStore("searchIds", {
  state: (): {
    searchIds: { [transactionType: string]: string[] };
    invalidFormatIds: { [transactionType: string]: string[] };
    validIds: { [transactionType: string]: string[] };
    invalidIds: { [transactionType: string]: string[] };
    assetApiError: { [transactionType: string]: { failed: boolean; message: string; params: any } };
    selectedContainers: { [transactionType: string]: string[] };
    transactionType: string;
    isScreenLoadingBack: boolean;
  } => ({
    searchIds: {},
    invalidFormatIds: {},
    validIds: {},
    invalidIds: {},
    assetApiError: {},
    selectedContainers: {},
    transactionType: "PUI",
    isScreenLoadingBack: false
  }),
  persist: false,
  getters: {
    // get search ids
    getSearchIds(): (transactionType?: string) => string[] {
      return (transactionType?: string): string[] => {
        transactionType = transactionType ?? this.transactionType;
        return this.searchIds[transactionType] ?? [];
      };
    },

    // get valid search ids
    getValidSearchIds(): (transactionType?: string) => string[] {
      return (transactionType?: string): string[] => {
        transactionType = transactionType ?? this.transactionType;
        return this.validIds[transactionType] ?? [];
      };
    },

    // get invalid search ids
    getInvalidSearchIds(): (transactionType?: string) => string[] {
      return (transactionType?: string): string[] => {
        transactionType = transactionType ?? this.transactionType;
        return this.invalidIds[transactionType] ?? [];
      };
    },

    getAssetApiError() {
      return (transactionType?: string) => {
        transactionType = transactionType ?? this.transactionType;
        return this.assetApiError[transactionType] ?? { failed: false, message: "", params: {} };
      };
    },
    getTerminalName(): string {
      const transactionType = this.transactionType ?? "NA";
      if (transactionType === "PUI") {
        const store = useImportLocationStore();
        return store.terminalSelected.id;
      } else if (transactionType === "DOE") {
        const store = useExportLocationStore();
        return store.terminalSelected.id;
      } else if (transactionType === "CEH") {
        const store = useEquipmentLocationStore();
        return store.terminalSelected.id;
      } else if (transactionType === "VS") {
        const store = useScheduleLocationStore();
        return store.terminalSelected.id;
      } else {
        return "NA";
      }
    }
  },
  actions: {
    // Set transaction type
    setTransactionType(transactionType: string): void {
      this.transactionType = transactionType ?? "";
    },
    // Add search ids to the store searchIds array
    addSearchId(id: string, transactionType?: string): void {
      transactionType = transactionType ?? this.transactionType;
      this.searchIds[transactionType] = Array.isArray(this.searchIds[transactionType])
        ? this.searchIds[transactionType]
        : [];
      if (!this.searchIds[transactionType].includes(id.trim()) && isValidString(id.trim())) {
        this.searchIds[transactionType].push(id.trim());
      }
    },

    // Remove search ids from the store searchIds array
    removeSearchId(id: string, transactionType?: string): void {
      transactionType = transactionType ?? this.transactionType;
      this.searchIds[transactionType] = this.searchIds[transactionType] ?? [];
      this.searchIds[transactionType] = this.searchIds[transactionType].filter(
        (searchId) => searchId.trim() !== id.trim()
      );
    },

    popSearchId(): void {
      const transactionType = this.transactionType;
      this.searchIds[transactionType].pop();
    },

    clearSearchIds(): void {
      this.searchIds = {};
    },

    setValidIds(ids: string[], transactionType?: string): void {
      transactionType = transactionType ?? this.transactionType;
      this.validIds[transactionType] = ids ? [...ids] : [];
    },

    setInvalidIds(ids: string[], transactionType?: string): void {
      transactionType = transactionType ?? this.transactionType;
      this.invalidIds[transactionType] = ids ? [...ids] : [];
    },

    // Set api error
    setAssetApiError(failed: boolean, message: string): void {
      this.assetApiError[this.transactionType] = { failed, message, params: {} };
    },

    // Clear the invalid ids array
    clearInvalidIds(): void {
      this.invalidIds = [];
    },

    // Clear all states
    clearAllStates(): void {
      this.invalidFormatIds = {};
      this.validIds = {};
      this.invalidIds = {};
      this.assetApiError = {};
      this.selectedContainers = {};
    },
    setScreenLoadingBack(val: boolean) {
      this.isScreenLoadingBack = val;
    }
  }
});
